import type { UserPolicy } from '@/types';
import { delay } from '@/utils/delay';
import { getIsCancelledError } from '@/utils/error';
import { fetchUserPolicies, filterSortPolicies } from '@/utils/policies';
import { useShallow } from 'zustand/react/shallow';
import { createWithDevtools } from './utils';

interface PoliciesStateProps {
  error: Error | null;
  hasError: boolean;
  isLoading: boolean;
  lastUpdated: number;
  data: {
    past: UserPolicy[];
    active: UserPolicy[];
    cancelled: UserPolicy[];
  };
}

interface PoliciesStateActions {
  resetPoliciesStatus: () => void;
  resetPoliciesData: () => void;
  fetchAllPolicies: () => Promise<void>;
}

type PoliciesState = PoliciesStateProps & PoliciesStateActions;

const initialStateProps: PoliciesStateProps = {
  error: null,
  hasError: false,
  isLoading: true,
  lastUpdated: Date.now(),
  data: {
    past: [],
    active: [],
    cancelled: [],
  },
};

const useStore = createWithDevtools<PoliciesState>(
  set => ({
    ...initialStateProps,
    resetPoliciesStatus: () => {
      const { error, hasError, isLoading } = initialStateProps;
      set({ error, hasError, isLoading });
    },
    resetPoliciesData: () => {
      const { data } = initialStateProps;
      set({ lastUpdated: Date.now(), data });
    },
    fetchAllPolicies: async (): Promise<void> => {
      set({ error: null, hasError: false, isLoading: true });
      const { data = [], error } = await fetchUserPolicies();
      await delay(300); // Delay is for smoother UX/animations.

      // Ignore previous/pending requests that are cancelled.
      if (getIsCancelledError(error)) {
        return;
      }

      if (error) {
        set({
          data: { ...initialStateProps.data },
          lastUpdated: Date.now(),
          isLoading: false,
          hasError: true,
          error,
        });
        return;
      }

      const { past, active, cancelled } = filterSortPolicies(data);

      set({
        data: { past, active, cancelled },
        lastUpdated: Date.now(),
        isLoading: false,
      });
    },
  }),
  'PoliciesStore',
);

// Memoized version of the store, using 'selector' with 'useShallow()'.
const usePoliciesStore = <T>(selector: (state: PoliciesState) => T) => {
  return useStore(useShallow(selector));
};

const selectPoliciesActions = (state: PoliciesState) => {
  return {
    resetPoliciesStatus: state.resetPoliciesStatus,
    resetPoliciesData: state.resetPoliciesData,
    fetchAllPolicies: state.fetchAllPolicies,
  };
};

const selectPastPolicies = (state: PoliciesState) => {
  return { pastPolicies: state.data.past, pastUpdated: state.lastUpdated };
};

const selectActivePolicies = (state: PoliciesState) => {
  return { activePolicies: state.data.active, activeUpdated: state.lastUpdated };
};

const selectCancelledPolicies = (state: PoliciesState) => {
  return { cancelledPolicies: state.data.cancelled, cancelledUpdated: state.lastUpdated };
};

const selectPoliciesStatus = (state: PoliciesState) => {
  return { error: state.error, hasError: state.hasError, isLoading: state.isLoading };
};

const selectRecentActivePolicy = (state: PoliciesState) => {
  return state.data.active?.[0] ?? null;
};

// Only to be used within test files.
export const policiesStore = useStore;

export {
  initialStateProps,
  selectActivePolicies,
  selectCancelledPolicies,
  selectPastPolicies,
  selectPoliciesActions,
  selectPoliciesStatus,
  selectRecentActivePolicy,
  usePoliciesStore,
};

export type { PoliciesState, PoliciesStateActions, PoliciesStateProps };
